/**
 * MCP Admin Panel
 * 
 * Central configuration interface for the Model Context Protocol server
 * Allows admins to configure MCP settings, manage tools, view logs, and more
 */

import React, { useState, useEffect } from 'react';
import {
  Settings,
  Plug,
  Activity,
  Shield,
  Zap,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Terminal,
  Bot,
  Eye,
  EyeOff,
  Copy,
  Check,
  Play,
  Pause,
  MessageSquare
} from 'lucide-react';
import { getMCPServer } from '../../mcp/server/MCPServer';
import { registerAllTools } from '../../mcp/tools';
import { useAuth } from '../../hooks/useAuth';
import { mcpConfigService, AVAILABLE_MCP_TOOLS, TOOL_CATEGORIES, type MCPOrgConfig } from '../../services/mcpConfigService';

interface MCPAdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'overview' | 'tools' | 'config' | 'logs' | 'testing' | 'documentation';

export const MCPAdminPanel: React.FC<MCPAdminPanelProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [mcpServer, setMcpServer] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [serverRunning, setServerRunning] = useState(false);
  const [tools, setTools] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);

  // Config State
  const [orgConfig, setOrgConfig] = useState<MCPOrgConfig | null>(null);
  const [mcpEnabled, setMcpEnabled] = useState(true);
  const [allowedTools, setAllowedTools] = useState<string[]>(['*']);
  const [requestsPerMinute, setRequestsPerMinute] = useState(60);
  const [requestsPerDay, setRequestsPerDay] = useState(10000);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  // Logs State
  const [logFilter, setLogFilter] = useState<'all' | 'debug' | 'info' | 'warning' | 'error'>('all');
  const [filteredLogs, setFilteredLogs] = useState<any[]>([]);

  // Testing State
  const [testTool, setTestTool] = useState('');
  const [testParams, setTestParams] = useState('{}');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      initializeMCP();
    }
  }, [isOpen]);

  const initializeMCP = async () => {
    try {
      setLoading(true);
      
      // Get or create MCP server instance
      const server = getMCPServer();
      setMcpServer(server);
      
      // Start server if not running
      if (!serverRunning) {
        await server.start();
        
        // Register all tools
        await registerAllTools();
        
        setServerRunning(true);
      }
      
      // Get health status
      const healthStatus = await server.getHealthStatus();
      setHealth(healthStatus);
      
      // Get tools
      const toolRegistry = (server as any).toolRegistry;
      const toolNames = toolRegistry.getToolNames();
      setTools(toolNames);
      
      // Get logs
      const logger = (server as any).logger;
      const recentLogs = logger.getRecentLogs(50);
      setLogs(recentLogs);
      
      // Load org config
      await loadOrgConfig();
      
      setLoading(false);
    } catch (error) {
      console.error('Error initializing MCP:', error);
      setLoading(false);
    }
  };

  const loadOrgConfig = async () => {
    try {
      const orgId = user?.profile?.orgId || user?.uid;
      if (!orgId) return;

      const config = await mcpConfigService.getOrgConfig(orgId);
      if (config) {
        setOrgConfig(config);
        setMcpEnabled(config.mcp.enabled);
        setAllowedTools(config.mcp.allowedTools);
        setRequestsPerMinute(config.limits?.requestsPerMinute || 60);
        setRequestsPerDay(config.limits?.requestsPerDay || 10000);
      }
    } catch (error) {
      console.error('Error loading org config:', error);
    }
  };

  const saveOrgConfig = async () => {
    try {
      setSavingConfig(true);
      const orgId = user?.profile?.orgId || user?.uid;
      const userId = user?.uid;

      if (!orgId || !userId) {
        throw new Error('Missing organization or user ID');
      }

      await mcpConfigService.updateOrgConfig(
        orgId,
        {
          mcp: {
            enabled: mcpEnabled,
            allowedTools: allowedTools
          },
          limits: {
            requestsPerMinute,
            requestsPerDay
          }
        },
        userId
      );

      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
      
      // Reload config
      await loadOrgConfig();
    } catch (error) {
      console.error('Error saving org config:', error);
      alert('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleToolSelection = (toolName: string) => {
    if (allowedTools.includes('*')) {
      // If wildcard is set, switch to individual tool selection
      setAllowedTools([toolName]);
    } else if (allowedTools.includes(toolName)) {
      // Remove tool
      const newTools = allowedTools.filter(t => t !== toolName);
      setAllowedTools(newTools.length > 0 ? newTools : ['*']);
    } else {
      // Add tool
      setAllowedTools([...allowedTools, toolName]);
    }
  };

  const selectAllTools = () => {
    setAllowedTools(['*']);
  };

  const deselectAllTools = () => {
    setAllowedTools([]);
  };

  const loadLogsData = () => {
    if (!mcpServer) return;
    
    try {
      const logger = (mcpServer as any).logger;
      const allLogs = logger?.getRecentLogs ? logger.getRecentLogs(100) : logs;
      
      if (logFilter === 'all') {
        setFilteredLogs(allLogs);
      } else {
        setFilteredLogs(allLogs.filter((log: any) => log.level === logFilter));
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setFilteredLogs([]);
    }
  };

  const clearLogs = () => {
    if (!mcpServer) return;
    
    try {
      const logger = (mcpServer as any).logger;
      if (logger?.clearLogs) {
        logger.clearLogs();
      }
      setFilteredLogs([]);
      setLogs([]);
    } catch (error) {
      console.error('Error clearing logs:', error);
    }
  };

  const formatTime = (timestamp: Date | any) => {
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '--:--:--';
    }
  };

  const logLevelColor = (level: string) => {
    const colors = {
      debug: 'text-txt-muted',
      info: 'text-info',
      warning: 'text-warning',
      error: 'text-danger'
    };
    return colors[level as keyof typeof colors] || 'text-txt-muted';
  };

  const runQuickTest = async (method: string, toolName?: string) => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const defaultParams: Record<string, any> = {
        'tools/list': {},
        'create_lead': { name: 'Test Lead', email: `test-${Date.now()}@example.com`, status: 'new' },
        'list_leads': { limit: 5 },
        'list_tasks': { limit: 5 }
      };
      
      const request: any = {
        jsonrpc: '2.0',
        id: Date.now(),
        method: method,
        params: {
          _context: {
            orgId: user?.profile?.orgId || user?.uid || 'test-org',
            userId: user?.uid || 'test-user'
          }
        }
      };
      
      if (toolName) {
        request.params.name = toolName;
        request.params.arguments = defaultParams[toolName] || {};
      }
      
      const response = await mcpServer.handleRequest(request);
      setTestResult(response);
    } catch (error: any) {
      setTestResult({ error: { code: -1, message: error.message } });
    } finally {
      setTesting(false);
    }
  };

  const runCustomTest = async () => {
    if (!testTool) {
      alert('Please select a tool');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const params = JSON.parse(testParams);
      
      const request = {
        jsonrpc: '2.0',
        id: Date.now(),
        method: 'tools/call',
        params: {
          _context: {
            orgId: user?.profile?.orgId || user?.uid || 'test-org',
            userId: user?.uid || 'test-user'
          },
          name: testTool,
          arguments: params
        }
      };
      
      const response = await mcpServer.handleRequest(request);
      setTestResult(response);
    } catch (error: any) {
      setTestResult({ error: { code: -32700, message: 'Parse error: ' + error.message } });
    } finally {
      setTesting(false);
    }
  };

  // Update filtered logs when filter changes
  React.useEffect(() => {
    loadLogsData();
  }, [logFilter, logs]);

  const handleStopServer = async () => {
    if (mcpServer) {
      await mcpServer.stop();
      setServerRunning(false);
    }
  };

  const handleStartServer = async () => {
    if (mcpServer) {
      await mcpServer.start();
      await registerAllTools();
      setServerRunning(true);
    }
  };

  const copyApiEndpoint = () => {
    const endpoint = `https://us-central1-aideline-pro.cloudfunctions.net/api/mcp`;
    navigator.clipboard.writeText(endpoint);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="p-6 border-b border-default bg-gradient-to-r from-accent to-accent text-txt-inverted">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-bg-card/20 rounded-lg backdrop-blur-sm">
                <Plug className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">MCP Admin Panel</h2>
                <p className="text-sm text-txt-inverted/80">Model Context Protocol Configuration</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Server Status */}
              <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg ${
                serverRunning ? 'bg-success/20' : 'bg-danger/20'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  serverRunning ? 'bg-success/80 animate-pulse' : 'bg-danger/80'
                }`} />
                <span className="text-sm font-medium">
                  {serverRunning ? 'Running' : 'Stopped'}
                </span>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-default bg-bg-surface px-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'tools', label: 'Tools', icon: Zap },
            { id: 'config', label: 'Configuration', icon: Settings },
            { id: 'logs', label: 'Logs', icon: Terminal },
            { id: 'testing', label: 'Testing', icon: Play },
            { id: 'documentation', label: 'Documentation', icon: Bot }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-txt-secondary hover:text-txt'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
                <p className="text-txt-secondary">Initializing MCP Server...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Status Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-accent to-info rounded-xl p-6 border border-accent">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-info">Server Status</h3>
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-3xl font-bold text-info">{serverRunning ? 'Healthy' : 'Stopped'}</p>
                      <p className="text-sm text-accent mt-1">
                        Uptime: {health?.uptime ? `${Math.floor(health.uptime / 1000 / 60)}min` : '0min'}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-success to-success rounded-xl p-6 border border-success">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-success">Registered Tools</h3>
                        <Zap className="w-5 h-5 text-success" />
                      </div>
                      <p className="text-3xl font-bold text-success">{tools.length}</p>
                      <p className="text-sm text-success mt-1">Available for agents</p>
                    </div>

                    <div className="bg-gradient-to-br from-accent to-accent rounded-xl p-6 border border-accent">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-accent">Total Requests</h3>
                        <Activity className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-3xl font-bold text-accent">{health?.metrics?.totalRequests || 0}</p>
                      <p className="text-sm text-accent mt-1">
                        Success: {health?.metrics?.successfulRequests || 0}
                      </p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h3 className="font-semibold text-txt mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={initializeMCP}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Refresh Status</span>
                      </button>
                      
                      <button
                        onClick={() => window.open('https://console.firebase.google.com/project/aideline-pro/functions', '_blank')}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent transition-colors"
                      >
                        <Activity className="w-4 h-4" />
                        <span>View Logs</span>
                      </button>
                    </div>
                    <p className="text-xs text-txt-tertiary mt-3">
                      💡 MCP server runs as a Firebase Cloud Function and scales automatically
                    </p>
                  </div>

                  {/* API Endpoint */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h3 className="font-semibold text-txt mb-4">Production API Endpoint</h3>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 px-4 py-3 bg-bg-surface rounded-lg text-sm font-mono text-txt">
                        https://us-central1-aideline-pro.cloudfunctions.net/api/mcp
                      </code>
                      <button
                        onClick={copyApiEndpoint}
                        className="p-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-sm text-txt-secondary mt-2">
                      Use this endpoint to connect external tools like n8n, Zapier, or Claude Desktop
                    </p>
                  </div>
                </div>
              )}

              {/* Tools Tab */}
              {activeTab === 'tools' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-txt">Registered Tools ({tools.length})</h3>
                  </div>
                  
                  {/* Group by Category */}
                  {TOOL_CATEGORIES.map(category => {
                    const categoryTools = tools.filter(toolName => {
                      const toolDef = AVAILABLE_MCP_TOOLS.find(t => t.name === toolName);
                      return toolDef?.category === category;
                    });
                    
                    if (categoryTools.length === 0) return null;
                    
                    return (
                      <div key={category} className="space-y-3">
                        <h4 className="text-sm font-semibold text-txt-secondary flex items-center space-x-2">
                          <Zap className="w-4 h-4" />
                          <span>{category} ({categoryTools.length})</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {categoryTools.map((toolName, index) => {
                            const toolDef = AVAILABLE_MCP_TOOLS.find(t => t.name === toolName);
                            const dangerColor = {
                              safe: { bg: 'bg-success-soft', text: 'text-success', border: 'border-success' },
                              moderate: { bg: 'bg-warning-soft', text: 'text-warning', border: 'border-warning' },
                              dangerous: { bg: 'bg-danger-soft', text: 'text-danger', border: 'border-danger' }
                            }[toolDef?.dangerLevel || 'safe'];
                            
                            return (
                              <div
                                key={index}
                                className="p-4 bg-bg-card rounded-lg border border-default hover:border-accent transition-colors"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <p className="font-medium text-txt">{toolName}</p>
                                      <CheckCircle className="w-4 h-4 text-success" />
                                    </div>
                                    <p className="text-xs text-txt-secondary mb-2">{toolDef?.description || 'No description'}</p>
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${dangerColor.bg} ${dangerColor.text} ${dangerColor.border} border`}>
                                      {toolDef?.dangerLevel || 'safe'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Unknown Tools (not in definition) */}
                  {(() => {
                    const unknownTools = tools.filter(toolName => 
                      !AVAILABLE_MCP_TOOLS.find(t => t.name === toolName)
                    );
                    
                    if (unknownTools.length === 0) return null;
                    
                    return (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-txt-secondary">Other ({unknownTools.length})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {unknownTools.map((toolName, index) => (
                            <div
                              key={index}
                              className="p-4 bg-bg-card rounded-lg border border-default"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-txt">{toolName}</p>
                                  <p className="text-xs text-txt-tertiary">No definition available</p>
                                </div>
                                <CheckCircle className="w-4 h-4 text-txt-muted" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Config Tab */}
              {activeTab === 'config' && (
                <div className="space-y-6">
                  {/* MCP Enable/Disable */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-txt">MCP Server Status</h3>
                        <p className="text-sm text-txt-secondary">Enable or disable the MCP server for your organization</p>
                      </div>
                      <button
                        onClick={() => setMcpEnabled(!mcpEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          mcpEnabled ? 'bg-success-soft' : 'bg-bg-surface-hover'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-bg-card transition-transform ${
                            mcpEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className={`flex items-center space-x-2 ${mcpEnabled ? 'text-success' : 'text-txt-muted'}`}>
                      {mcpEnabled ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">MCP Enabled</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">MCP Disabled</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Allowed Tools */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-txt">Allowed Tools</h3>
                        <p className="text-sm text-txt-secondary">Select which tools can be accessed via MCP</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={selectAllTools}
                          className="px-3 py-1 text-sm bg-accent-soft text-accent rounded-lg hover:bg-accent-soft transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          onClick={deselectAllTools}
                          className="px-3 py-1 text-sm bg-bg-surface text-txt-secondary rounded-lg hover:bg-bg-surface-hover transition-colors"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    {allowedTools.includes('*') && (
                      <div className="mb-4 p-3 bg-success-soft border border-success rounded-lg flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="text-sm text-success font-medium">All tools are enabled (wildcard mode)</span>
                      </div>
                    )}

                    <div className="space-y-3">
                      {TOOL_CATEGORIES.map(category => (
                        <div key={category} className="space-y-2">
                          <h4 className="text-sm font-semibold text-txt-secondary">{category}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {AVAILABLE_MCP_TOOLS.filter(t => t.category === category).map(tool => {
                              const isSelected = allowedTools.includes('*') || allowedTools.includes(tool.name);
                              const dangerColor = {
                                safe: 'green',
                                moderate: 'yellow',
                                dangerous: 'red'
                              }[tool.dangerLevel];

                              return (
                                <button
                                  key={tool.name}
                                  onClick={() => toggleToolSelection(tool.name)}
                                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                                    isSelected
                                      ? 'border-accent bg-accent-soft'
                                      : 'border-default bg-bg-card hover:border-strong'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <p className={`text-sm font-medium ${isSelected ? 'text-accent' : 'text-txt'}`}>
                                        {tool.name}
                                      </p>
                                      <p className="text-xs text-txt-secondary mt-0.5">{tool.description}</p>
                                    </div>
                                    <div className="flex items-center space-x-2 ml-2">
                                      <span className={`text-xs px-2 py-0.5 rounded bg-${dangerColor}-100 text-${dangerColor}-700`}>
                                        {tool.dangerLevel}
                                      </span>
                                      {isSelected && <CheckCircle className="w-4 h-4 text-accent" />}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rate Limits */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h3 className="text-lg font-semibold text-txt mb-4">Rate Limits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-txt-secondary mb-2">
                          Requests per Minute
                        </label>
                        <input
                          type="number"
                          value={requestsPerMinute}
                          onChange={(e) => setRequestsPerMinute(parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-strong rounded-lg focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                          min="1"
                          max="1000"
                        />
                        <p className="text-xs text-txt-tertiary mt-1">Default: 60</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-txt-secondary mb-2">
                          Requests per Day
                        </label>
                        <input
                          type="number"
                          value={requestsPerDay}
                          onChange={(e) => setRequestsPerDay(parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-strong rounded-lg focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                          min="1"
                          max="100000"
                        />
                        <p className="text-xs text-txt-tertiary mt-1">Default: 10,000</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center justify-between">
                    <div>
                      {configSaved && (
                        <div className="flex items-center space-x-2 text-success">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Configuration saved successfully!</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={saveOrgConfig}
                      disabled={savingConfig}
                      className="flex items-center space-x-2 px-6 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                    >
                      {savingConfig ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Settings className="w-4 h-4" />
                          <span>Save Configuration</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Logs Tab */}
              {activeTab === 'logs' && (
                <div className="space-y-4">
                  {/* Filter Bar */}
                  <div className="flex items-center justify-between bg-bg-card rounded-xl p-4 border border-default">
                    <div className="flex space-x-2">
                      {(['all', 'debug', 'info', 'warning', 'error'] as const).map(level => (
                        <button
                          key={level}
                          onClick={() => setLogFilter(level)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            logFilter === level 
                              ? 'bg-accent text-txt-inverted' 
                              : 'bg-bg-surface text-txt-secondary hover:bg-bg-surface-hover'
                          }`}
                        >
                          {level.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={loadLogsData}
                        className="p-2 bg-info-soft text-accent rounded-lg hover:bg-info-soft transition-colors"
                        title="Refresh Logs"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={clearLogs}
                        className="px-3 py-2 bg-danger-soft text-danger rounded-lg hover:bg-danger-soft transition-colors text-sm font-medium"
                      >
                        Clear Logs
                      </button>
                    </div>
                  </div>

                  {/* Log Entries */}
                  <div className="bg-bg-raised text-txt-muted rounded-xl p-4 font-mono text-xs space-y-1 max-h-96 overflow-y-auto">
                    {filteredLogs.length === 0 ? (
                      <div className="text-center py-8 text-txt-tertiary">
                        <Terminal className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No logs available</p>
                        <p className="text-xs mt-1">Logs will appear here as MCP server processes requests</p>
                      </div>
                    ) : (
                      filteredLogs.map((log, i) => (
                        <div key={i} className={`flex space-x-3 py-1 ${logLevelColor(log.level)}`}>
                          <span className="text-txt-tertiary w-20 flex-shrink-0">{formatTime(log.timestamp)}</span>
                          <span className="font-bold w-16 flex-shrink-0">[{(log.level || 'info').toUpperCase()}]</span>
                          <span className="flex-1 break-all">{log.message}</span>
                          {log.context && (
                            <span className="text-txt-secondary text-xs">
                              {typeof log.context === 'object' ? JSON.stringify(log.context).slice(0, 50) : log.context}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Log Stats */}
                  <div className="bg-bg-card rounded-xl p-4 border border-default">
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-txt">{filteredLogs.length}</p>
                        <p className="text-xs text-txt-secondary">Total Logs</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-danger">
                          {filteredLogs.filter(l => l.level === 'error').length}
                        </p>
                        <p className="text-xs text-txt-secondary">Errors</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-warning">
                          {filteredLogs.filter(l => l.level === 'warning').length}
                        </p>
                        <p className="text-xs text-txt-secondary">Warnings</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-accent">
                          {filteredLogs.filter(l => l.level === 'info').length}
                        </p>
                        <p className="text-xs text-txt-secondary">Info</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Testing Tab */}
              {activeTab === 'testing' && (
                <div className="space-y-6">
                  {/* Quick Tests */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h3 className="font-semibold text-txt mb-4">Quick Tests</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => runQuickTest('tools/list')}
                        disabled={testing}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright transition-colors disabled:opacity-50"
                      >
                        <Zap className="w-4 h-4" />
                        <span>Test Tools List</span>
                      </button>
                      <button
                        onClick={() => runQuickTest('tools/call', 'create_lead')}
                        disabled={testing}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-success text-txt-inverted rounded-lg hover:bg-success/80 transition-colors disabled:opacity-50"
                      >
                        <Play className="w-4 h-4" />
                        <span>Test Create Lead</span>
                      </button>
                      <button
                        onClick={() => runQuickTest('tools/call', 'list_leads')}
                        disabled={testing}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                      >
                        <Activity className="w-4 h-4" />
                        <span>Test List Leads</span>
                      </button>
                      <button
                        onClick={() => runQuickTest('tools/call', 'list_tasks')}
                        disabled={testing}
                        className="flex items-center justify-center space-x-2 px-4 py-3 bg-warning-soft text-txt-inverted rounded-lg hover:bg-warning-soft transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Test List Tasks</span>
                      </button>
                    </div>
                  </div>

                  {/* Custom Test */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h3 className="font-semibold text-txt mb-4">Custom Tool Test</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-txt-secondary mb-1">
                          Select Tool
                        </label>
                        <select
                          value={testTool}
                          onChange={(e) => setTestTool(e.target.value)}
                          className="w-full px-3 py-2 bg-bg-input border border-default rounded-input text-[13px] text-txt focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                        >
                          <option value="">-- Select a tool --</option>
                          {tools.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-txt-secondary mb-1">
                          Parameters (JSON)
                        </label>
                        <textarea
                          value={testParams}
                          onChange={(e) => setTestParams(e.target.value)}
                          placeholder='{"name": "Test Lead", "email": "test@example.com"}'
                          className="w-full px-3 py-2 bg-bg-input border border-default rounded-input text-[13px] text-txt focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-transparent font-mono text-sm"
                          rows={6}
                        />
                      </div>
                      <button
                        onClick={runCustomTest}
                        disabled={testing || !testTool}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-txt-inverted rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                      >
                        {testing ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Testing...</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            <span>Run Test</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Test Results */}
                  {testResult && (
                    <div className="bg-bg-card rounded-xl p-6 border border-default">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-txt">Test Result</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          testResult.error 
                            ? 'bg-danger-soft text-danger' 
                            : 'bg-success-soft text-success'
                        }`}>
                          {testResult.error ? 'FAILED' : 'SUCCESS'}
                        </span>
                      </div>
                      <div className={`p-4 rounded-lg ${
                        testResult.error ? 'bg-danger-soft border border-danger' : 'bg-success-soft border border-success'
                      }`}>
                        <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(testResult, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documentation Tab */}
              {activeTab === 'documentation' && (
                <div className="space-y-4">
                  {/* Quick Start */}
                  <div className="bg-gradient-to-br from-accent to-info rounded-xl p-6 border border-accent">
                    <div className="flex items-center space-x-3 mb-4">
                      <Plug className="w-6 h-6 text-accent" />
                      <h3 className="text-lg font-semibold text-txt">Getting Started</h3>
                    </div>
                    <p className="text-txt-secondary mb-4">
                      Learn how to set up and use the MCP server with external AI agents and automation tools.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <a
                        href="https://github.com/anthropics/mcp"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-bg-card rounded-lg hover:bg-info-soft transition-colors border border-accent"
                      >
                        <span className="font-medium text-txt">MCP Specification</span>
                        <Activity className="w-4 h-4 text-accent" />
                      </a>
                      <button
                        onClick={() => window.open('https://console.firebase.google.com/project/aideline-pro/functions', '_blank')}
                        className="flex items-center justify-between p-3 bg-bg-card rounded-lg hover:bg-info-soft transition-colors border border-accent"
                      >
                        <span className="font-medium text-txt">View Firebase Logs</span>
                        <Terminal className="w-4 h-4 text-accent" />
                      </button>
                    </div>
                  </div>

                  {/* Integration Guides */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h4 className="font-semibold text-txt mb-4 flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-accent" />
                      <span>Integration Guides</span>
                    </h4>
                    <div className="space-y-3">
                      <div className="p-4 bg-bg-surface rounded-lg">
                        <h5 className="font-medium text-txt mb-2">Claude Desktop Integration</h5>
                        <p className="text-sm text-txt-secondary mb-3">
                          Connect Claude Desktop to your Aideline Pro MCP server
                        </p>
                        <code className="block p-3 bg-bg-raised text-txt-muted rounded text-xs overflow-x-auto">
                          {`{
  "mcpServers": {
    "aideline-pro": {
      "url": "https://us-central1-aideline-pro.cloudfunctions.net/api/mcp",
      "headers": {
        "x-api-key": "YOUR_API_KEY"
      }
    }
  }
}`}
                        </code>
                      </div>

                      <div className="p-4 bg-bg-surface rounded-lg">
                        <h5 className="font-medium text-txt mb-2">n8n Workflow</h5>
                        <p className="text-sm text-txt-secondary mb-2">
                          Use HTTP Request node with these settings:
                        </p>
                        <ul className="text-sm text-txt-secondary space-y-1">
                          <li>• Method: POST</li>
                          <li>• URL: https://us-central1-aideline-pro.cloudfunctions.net/api/mcp</li>
                          <li>• Header: x-api-key = YOUR_API_KEY</li>
                          <li>• Body: JSON-RPC 2.0 format</li>
                        </ul>
                      </div>

                      <div className="p-4 bg-bg-surface rounded-lg">
                        <h5 className="font-medium text-txt mb-2">API Testing with curl</h5>
                        <code className="block p-3 bg-bg-raised text-txt-muted rounded text-xs overflow-x-auto">
                          {`curl -X POST https://us-central1-aideline-pro.cloudfunctions.net/api/mcp \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/list",
    "params": {}
  }'`}
                        </code>
                      </div>
                    </div>
                  </div>

                  {/* Quick Tips */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h4 className="font-semibold text-txt mb-3">💡 Quick Tips</h4>
                    <ul className="space-y-2 text-sm text-txt-secondary">
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span>Generate API keys in <strong>Settings → API Management</strong></span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span>Configure allowed tools in the <strong>Config</strong> tab</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span>Test tools locally in the <strong>Testing</strong> tab before external integration</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span>Monitor requests in real-time via <strong>Logs</strong> tab</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span>Rate limits: 60 req/min, 10k req/day (configurable)</span>
                      </li>
                    </ul>
                  </div>

                  {/* Available Tools Summary */}
                  <div className="bg-bg-card rounded-xl p-6 border border-default">
                    <h4 className="font-semibold text-txt mb-3">📚 Available Tools ({tools.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {TOOL_CATEGORIES.map(category => {
                        const count = tools.filter(t => 
                          AVAILABLE_MCP_TOOLS.find(def => def.name === t && def.category === category)
                        ).length;
                        return (
                          <div key={category} className="p-3 bg-bg-surface rounded-lg text-center">
                            <p className="text-2xl font-bold text-accent">{count}</p>
                            <p className="text-xs text-txt-secondary">{category}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

